<template>
  <v-card
  flat
  height="100%"
  id="playlistBadgeCreatorCard"
  tabindex="-1"
  ref="createBadgeCard"
  :ripple="false">
    <v-list
    :color="playlistColor"
    height="65"
    dark
    tile>
      <v-list-item :color="playlistColor">
        <v-list-item-avatar>
          <v-btn icon @click="closeSlideout" aria-label="Close add attachment tab">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title
            class="playlist-headline">Create new badge.
            <v-icon class="ml-2">mdi-shield-star-outline</v-icon>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-container>
      <v-card
        flat
        class="mt-4"
        :ripple="false">
        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                cols=8
                class="pb-0">
                <v-file-input
                  v-model="badgeImage"
                  :clearable=false
                  :rules="badgeRules"
                  :disabled="isUpdating"
                  filled
                  dense
                  accept="image/png, image/svg+xml"
                  placeholder="Upload a badge image"
                  label="Upload badge image"
                  ref="microcredentialBadgeUploader"
                  hide-details="auto"
                  class="mb-4 file-input"
                  @change="handleImageAdded"
                ></v-file-input>
              </v-col>
              <v-col
                class="pt-0">
                <v-img
                  v-if="badgeImage"
                  contain
                  height="100"
                  :src="fileImagePreviewURL"></v-img>
              </v-col>
            </v-row>
            <v-row>
            <v-col cols=12>
              <v-text-field
                prepend-icon="mdi-badge-account-horizontal-outline"
                v-model.trim="badgeName"
                :color="playlistColor"
                :error-messages="badgeNameErrors"
                label="Badge Name"
                @input="$v.badgeName.$touch()">
              </v-text-field>
              <v-textarea
                prepend-icon="mdi-text-long"
                rows="3"
                auto-grow
                row-height="25"
                counter
                v-model.trim="badgeDescription"
                :color="playlistColor"
                :error-messages="badgeDescriptionErrors"
                label="Badge Description"
                @input="$v.badgeDescription.$touch()">
              </v-textarea>
              <v-textarea
                v-if="!useBadgeCriteriaURL"
                prepend-icon="mdi-shield-check-outline"
                rows="3"
                auto-grow
                row-height="25"
                v-model.trim="badgeCriteriaText"
                :color="playlistColor"
                :error-messages="badgeCriteriaTextErrors"
                label="Badge Criteria (detailed text)"
                @input="$v.badgeCriteriaText.$touch()">
              </v-textarea>
              <v-text-field
                v-else
                prepend-icon="mdi-shield-check-outline"
                v-model.trim="badgeCriteriaURL"
                :color="playlistColor"
                :error-messages="badgeCriteriaURLErrors"
                label="Badge Criteria (URL)"
                @input="$v.badgeCriteriaURL.$touch()">
              </v-text-field>
              <v-checkbox
                class="mt-0 pt-0"
                v-model="useBadgeCriteriaURL"
                label="Use criteria URL"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :color="playlistColor"
            class="white--text"
            tile
            min-width="100"
            :disabled="!createBadgeValid"
            @click="handleCreateBadge"
            ref="createBadgeButton"
            aria-label="Create Badge"
            :loading="isUpdating">
            Create Badge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
} from 'vuex';
import _ from 'lodash';
import {
  required,
  maxLength,
  url,
} from 'vuelidate/lib/validators';

import imageToBase64 from 'image-to-base64/browser';
import imageCompression from 'browser-image-compression';

export default {
  name: 'BadgeCreator',
  props: {
    issuerEntityId: String,
  },
  data: () => ({
    badgeCriteriaText: null,
    useBadgeCriteriaURL: false,
    badgeCriteriaURL: null,
    badgeDescription: null,
    badgeImage: null,
    badgeName: null,
    badgeRules: [
      value => !value || value.size < 25000000 || 'File size should be less than 25 MB!',
      value => !value || value.type.includes('image/png') || value.type.includes('image/svg+xml') || 'Only images (.png or .svg) are allowed!',
    ],
  }),
  computed: {
    ...mapState([
      'isUpdating',
      'playlist',
      'slideoutData',
    ]),
    badgeNameErrors() {
      const errors = [];
      if (!this.$v.badgeName.$dirty) return errors;
      if (!this.$v.badgeName.required) {
        errors.push('Badge name is required.');
      }
      if (!this.$v.badgeName.maxLength) {
        errors.push('Badge name max length is 255 characters.');
      }
      return errors;
    },
    badgeCriteriaTextErrors() {
      const errors = [];
      if (!this.$v.badgeCriteriaText.$dirty) return errors;
      if (!this.$v.badgeCriteriaText.required) {
        errors.push('Badge criteria is required.');
      }
      if (!this.$v.badgeCriteriaText.maxLength) {
        errors.push('Badge criteria max length is 255 characters.');
      }
      return errors;
    },
    badgeCriteriaURLErrors() {
      const errors = [];
      if (!this.$v.badgeCriteriaURL.$dirty) return errors;
      if (!this.$v.badgeCriteriaURL.required) {
        errors.push('Badge criteria is required.');
      }
      if (!this.$v.badgeCriteriaURL.url) {
        errors.push('Must be a valid URL and include https://');
      }
      return errors;
    },
    badgeDescriptionErrors() {
      const errors = [];
      if (!this.$v.badgeDescription.$dirty) return errors;
      if (!this.$v.badgeDescription.required) {
        errors.push('Badge description is required.');
      }
      if (!this.$v.badgeDescription.maxLength) {
        errors.push('Badge name max length is 255 characters.');
      }
      return errors;
    },
    createBadgeValid() {
      return (
        (
          this.$v.badgeCriteriaText.$dirty
          || this.$v.badgeCriteriaURL.$dirty
        )
        && this.$v.badgeDescription.$dirty
        && this.$v.badgeName.$dirty
        && !this.$v.$anyError
        && this.badgeImage
      );
    },
    fileImagePreviewURL() {
      return this.badgeImage ? URL.createObjectURL(this.badgeImage) : null;
    },
    playlistColor() {
      return _.get(this.playlist, 'default_color', '#2196F3');
    },
  },
  methods: {
    ...mapActions([
      'createBadgrBadgeClass',
    ]),
    ...mapMutations([
      'setBadgrLoaded',
      'setIsUpdating',
      'setPlaylist',
      'setSlideout',
      'toggleSnackbar',
    ]),
    closeSlideout() {
      this.setSlideout(null);
    },
    async handleCreateBadge() {
      try {
        this.setIsUpdating(true);
        const imageType = this.badgeImage.name.split('.').pop();
        const dataURL = await imageToBase64(this.fileImagePreviewURL);
        const badge = {
          name: this.badgeName,
          image: `data:image/${imageType === 'svg' ? 'svg+xml' : imageType};base64,${dataURL}`,
          description: this.badgeDescription,
        };
        if (this.useBadgeCriteriaURL) {
          badge.criteriaUrl = this.badgeCriteriaURL;
        } else {
          badge.criteriaNarrative = this.badgeCriteriaText;
        }
        const badgeData = {
          badge,
          playlistId: this.playlist.id,
          issuerEntityId: this.issuerEntityId,
        };
        const { data: updatedPlaylist } = await this.createBadgrBadgeClass(badgeData);
        this.setPlaylist(updatedPlaylist);
        this.setSlideout(null);
        this.setBadgrLoaded(true);
        this.toggleSnackbar({
          text: 'Badgr badge successfully created!',
          color: 'success',
          timeout: 2500,
        });
      } catch (e) {
        this.toggleSnackbar({
          text: 'Error creating Badgr badge.',
          color: 'error',
          timeout: 2500,
        });
      } finally {
        this.setIsUpdating(false);
      }
    },
    async handleImageAdded() {
      const options = {
        maxWidthOrHeight: 400,
      };
      try {
        this.badgeImage = await imageCompression(this.badgeImage, options);
      } catch (error) {
        this.toggleSnackbar({
          text: 'Failed to compress badge image - attempting with original.',
          color: 'warning',
          timeout: 5000,
        });
      }
    },
  },
  validations: {
    badgeName: {
      maxLength: maxLength(255),
      required,
    },
    badgeDescription: {
      required,
      maxLength: maxLength(255),
    },
    badgeCriteriaText: {
      required,
      maxLength: maxLength(255),
    },
    badgeCriteriaURL: {
      required,
      url,
    },
  },
  watch: {
    async slideoutData(n) {
      if (n && n.type === 'badgeCreator') {
        await this.$nextTick();
        setTimeout(() => {
          this.$refs.createBadgeCard.$el.focus();
        }, 100);
      }
    },
  },
};
</script>

<style scoped>
  .aria-feedback {
    text-indent: -10000px;
    font-size: 0.1px;
  }
</style>
